<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { InputCell, SelectCell } from '@/components/common/element-plus/ElTableV2'
import RlEmpty from '@/components/common/RlEmptyDataset.vue'
import RlErrorResult from '@/components/common/RlErrorResult.vue'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcDisplaySerialNumber from '@/components/rc/common/RcDisplaySerialNumber.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { useCBModelOptions } from '@/composables/rc/control-board-model-options'
import { useHashboardModelOptions } from '@/composables/rc/hashboard-model-options'
import { useMinerModelOptions } from '@/composables/rc/miner-model-options'
import { usePSUModelOptions } from '@/composables/rc/power-supply-model-options'
import type { ManagedRow } from '@/composables/table-row-inline-edit'
import { getMiners, updateMinerParts } from '@/services/rc.service'
import { deleteMiner } from '@/services/rc/miners.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { ICustomer } from '@/types/core/Customer'
import type { IMinerDashboardFilters } from '@/types/rc/filters'
import type { IMinerPartUpdateForm } from '@/types/rc/forms'
import type { IControlBoard, IHashboard, IMinerDetails, IMinerModel, IModel, IPowerSupply } from '@/types/rc/miners'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { generateModelDisplayName, generateModelValueKey } from '@/util/rc'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElButton, ElMessage, ElOption, TableV2FixedDir, type Column, type InputInstance } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import _, { get, initial } from 'lodash'
import diff from 'microdiff'
import { storeToRefs } from 'pinia'
import { computed, h, reactive, ref, toRefs, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Search } from '@element-plus/icons-vue'
import { useDebounceFn } from '@vueuse/core'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'
import RlMinerModelSelect from '@/components/common/RlMinerModelSelect.vue'

// Technical debt, the general parts should support create via undefined fields.
// However, I don't want to cause type check errors across the app, so...
const queryClient = useQueryClient()

const oidcStore = useOIDCStore()
const { isRcAppManagerUser } = storeToRefs(oidcStore)

interface IMinersTablePSU {
  model: IModel | null
  sn: string | null
  has_been_in_rc_before: boolean
}

interface IMinersTableHB {
  model: IModel | null
}

interface IMinersTableCB {
  model: IModel | null
  sn: string | null
  has_been_in_rc_before: boolean
}

interface IMinersTableRowData extends Omit<IMinerDetails, 'fans' | 'hb_model' | 'psu' | 'cb'> {
  psu: IMinersTablePSU | null
  hb: IMinersTableHB | null
  cb: IMinersTableCB | null
}

const props = withDefaults(
  defineProps<{
    workOrderNumber?: string
    editable?: boolean
  }>(),
  {
    workOrderNumber: undefined,
    editable: true,
  }
)

const { editable } = toRefs(props)

const router = useRouter()
const { query: routeQueryParams } = router.currentRoute.value

const partSnRef = ref<string>()
const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'SN, MAC or Asset Tag',
  style: 'width: 248px;',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

// Filters
const filters = reactive<IMinerDashboardFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  work_order_number: props.workOrderNumber ?? routeQueryParams.workOrderNumber?.toString(),
  models: undefined,
  customer_id: undefined,
  part_sn: undefined,
  fragment: undefined,
})

// Table
const tableRef = ref()
const tableData = ref<IMinersTableRowData[]>([])
const editingRowIndex = ref<number | null>(null)
const tableIsEditMode = computed(() => {
  return !!editingRowIndex.value || editingRowIndex.value === 0
})

// Calculate table height since it's a fixed-height table
const tableHeight = computed(() => 50 * (tableData.value.length + 1) + 8) // 50px per row + 1 for header + 8 for scrollbar

// Pull miners data
const { isFetching, isError, data, error, isRefetching, refetch } = useQuery({
  queryKey: ['miners', filters],
  queryFn: () =>
    getMiners(filters).then((minersPage) => {
      tableData.value = minersPage.data?.map((miner) => transformMinerForTable(miner)) ?? []
      return minersPage
    }),
  refetchOnWindowFocus: () => !tableIsEditMode.value,
})

/**
 * Transforms a miner details object.
 *
 * @param {IMinerDetails} miner miner details.
 */
const transformMinerForTable = (miner: IMinerDetails): IMinersTableRowData => {
  const coalescedHashboards = Array(4).fill(null)
  for (let i = 0; i < coalescedHashboards.length; i++) {
    const hashboard = miner.hashboards.find((hb) => hb.number === i)
    if (hashboard) {
      coalescedHashboards[i] = hashboard
    }
  }

  // Workaround: the response from /miners isn't consistent with model select types
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hashboards, psu, cb, hb_model, fans, ...restOfDetails } = miner // removes "hb_model", "fans"
  return {
    ...restOfDetails,
    psu: transformPSUForTable(psu),
    hb: transformHBForTable(hashboards),
    cb: transformCBForTable(cb),
    hashboards: coalescedHashboards,
  }
}

/**
 * Get CB model and model version from a CB in "model options" format.
 *
 * @param miner
 */
const transformCBForTable = (cb: IControlBoard | null): IMinersTableCB => {
  return {
    model: cb?.model
      ? ({
          name: cb.model,
          version: cb.model_version,
          valueKey: cb.model && generateModelValueKey(cb.model, cb.model_version),
        } as IModel)
      : null,
    sn: cb?.sn ?? null,
    has_been_in_rc_before: cb?.has_been_in_rc_before ?? false,
  }
}

/**
 * Get PSU model and model version from a PSU in "model options" format.
 *
 * @param miner
 */
const transformPSUForTable = (psu: IPowerSupply | null): IMinersTablePSU => {
  return {
    model: psu?.model
      ? ({
          name: psu.model,
          version: psu.model_version,
          valueKey: psu.model && generateModelValueKey(psu.model, psu.model_version),
        } as IModel)
      : null,
    sn: psu?.sn ?? null,
    has_been_in_rc_before: psu?.has_been_in_rc_before ?? false,
  }
}

/**
 * Gets HB model and version from a miner in "model options" format.
 *
 * @param {IHashboard[]} hashboards miner hashboards array.
 */
const transformHBForTable = (hashboards: IHashboard[]): IMinersTableHB => {
  let mostCompleteHashboard: IModel = { name: null, version: null }
  for (const hashboard of hashboards) {
    if (hashboard.model && hashboard.model_version) {
      mostCompleteHashboard = { name: hashboard.model, version: hashboard.model_version }
      break
    } else if (hashboard.model) {
      mostCompleteHashboard = { name: hashboard.model, version: null }
    }
  }

  return {
    model: mostCompleteHashboard.name
      ? ({
          ...mostCompleteHashboard,
          valueKey:
            !!mostCompleteHashboard.name &&
            generateModelValueKey(mostCompleteHashboard.name, mostCompleteHashboard.version),
        } as IModel)
      : null,
  }
}

// Model options
const { minerModelNameOptions: minerModelOptions, isError: minerModelOptionsIsError } = useMinerModelOptions({
  enabled: editable,
})
const { data: psuModelOptions, isError: psuModelOptionsIsError } = usePSUModelOptions({ enabled: editable })
const { data: cbModelOptions, isError: cbModelOptionsIsError } = useCBModelOptions({ enabled: editable })
const { data: hashboardModelOptions, isError: hashboardModelOptionsIsError } = useHashboardModelOptions({
  enabled: editable,
})

watch(minerModelOptionsIsError, () => {
  if (minerModelOptionsIsError.value) {
    ElMessage.error('Failed to fetch miner model options.')
  }
})

watch(psuModelOptionsIsError, () => {
  if (psuModelOptionsIsError.value) {
    ElMessage.error('Failed to fetch PSU model options.')
  }
})

watch(cbModelOptionsIsError, () => {
  if (cbModelOptionsIsError.value) {
    ElMessage.error('Failed to fetch CB model options.')
  }
})

watch(hashboardModelOptionsIsError, () => {
  if (hashboardModelOptionsIsError.value) {
    ElMessage.error('Failed to fetch hashboard model options.')
  }
})

// Update miner mutation
const { mutateAsync: updateMinerAsync } = useMutation({
  mutationFn: (updatedMiner: IMinerPartUpdateForm) => updateMinerParts(updatedMiner),
  mutationKey: ['update-miner'],
})

// Delete miner mutation
const { mutateAsync: deleteMinerAsync } = useMutation({
  mutationFn: (id: number) => deleteMiner(id),
  onError: (e: Error) => {
    NotifyFail(e.message)
  },
  onSuccess: async () => {
    NotifySuccess(`Miner deleted successfully`)
    queryClient.invalidateQueries({ queryKey: ['miners', filters] })
  },
})

interface CellRendererProps {
  rowIndex: number
  rowData: ManagedRow<IMinersTableRowData>
  column: Column
}

/**
 * Renders a miner table cell.
 *
 * @param param0 ElTableV2 table row context.
 */
const minersTableCellRenderer = ({ rowIndex, rowData, column }: CellRendererProps) => {
  const cellKey = `${rowIndex}-${String(column.dataKey)}`
  const dataKey = column.dataKey as keyof ManagedRow<IMinersTableRowData>

  /**
   * Handle edit mode cancellation.
   */
  const onCancelEditMode = () => {
    editingRowIndex.value = null
    resetTableRow({ rowData })
  }

  /**
   * Clear status class on status animation end or status animation cancel.
   */
  const onAnimationEndOrCancel = () => {
    rowData.updated = null
  }

  const input = ref()
  const setRef = (el: InputInstance) => {
    input.value = el
  }

  /**
   * Renders a select cell (using `ElSelect`).
   *
   * @param {OptionType[] | undefined} options options to render in select.
   */
  const renderSelectCell = (options: OptionType[] = [], valueKey?: string) =>
    h(SelectCell, {
      key: cellKey,
      forwardRef: setRef,
      value: valueKey ? _.get(rowData, column.dataKey!) : rowData[dataKey],
      onChange: (newValue: any) =>
        valueKey ? _.set(rowData, column.dataKey!, newValue) : ((rowData as any)[dataKey] = newValue),
      onKeydownCapture: ({ key }: any) => key === 'Escape' && onCancelEditMode(),
      options: () => (options ? options.map((opt) => h(ElOption, { label: opt.label, value: opt.value })) : []),
      valueKey,
      clearable: column.key !== 'model',
      valueOnClear: null,
    })

  /**
   * Renders a text input cell.
   *
   * @param path (optional) path for object key values.
   */
  const renderInputCell = (path?: string[] | string) => {
    return h(InputCell, {
      key: cellKey,
      forwardRef: setRef,
      value: path ? _.get(rowData, path) : rowData[dataKey],
      onChange: (newValue: string) => (path ? _.set(rowData, path, newValue) : ((rowData as any)[dataKey] = newValue)),
      onKeydown: ({ key }: any) =>
        (key === 'Escape' && onCancelEditMode()) || (key === 'Enter' && onExitEditMode({ rowData })),
      mask: column.dataKey === 'mac_address' ? '##:##:##:##:##:##' : undefined,
      maskTokens: column.dataKey === 'mac_address' ? '#:[0-9a-fA-F]' : undefined,
    })
  }

  /**
   * Renders a display cell (read mode).
   */
  const renderDisplayCell = () => {
    const displayValue = getDisplayValue({ column, rowData, rowIndex })
    return h(
      'div',
      {
        key: column.dataKey,
        style: 'flex: 1 1 auto',
        class: displayValue === 'N/A' ? 'nonpresent-part' : rowData.updated,
        onAnimationend: onAnimationEndOrCancel,
        onAnimationcancel: onAnimationEndOrCancel,
      },
      displayValue || '-'
    )
  }

  /**
   * Renders a max-width with tooltip cell.
   */
  const renderSnDisplayCell = (type: 'miner' | 'part') => {
    const displayValue = getDisplayValue({ column, rowData, rowIndex })
    const isRecurrent = getIsRecurrent({ column, rowData })

    return h(RcDisplaySerialNumber, {
      key: column.dataKey,
      sn: displayValue,
      threshold: 16,
      recurrent: isRecurrent,
      type,
      style: 'flex: 1 1 auto',
      class: displayValue === 'N/A' ? 'nonpresent-part' : rowData.updated,
    })
  }

  if (editingRowIndex.value === rowIndex && column.editable) {
    switch (column.dataKey) {
      case 'model':
        return renderSelectCell(minerModelOptions.value)
      case 'psu.model':
        return renderSelectCell(psuModelOptions.value, 'valueKey')
      case 'hb.model':
        return renderSelectCell(hashboardModelOptions.value, 'valueKey')
      case 'cb.model':
        return renderSelectCell(cbModelOptions.value, 'valueKey')
      case 'psu.sn':
      case 'cb.sn':
        return renderInputCell(column.dataKey!)
      case 'hashboards.0.sn':
      case 'hashboards.1.sn':
      case 'hashboards.2.sn':
      case 'hashboards.3.sn':
        return rowData.hashboards[column.hbNumber] ? renderInputCell(column.dataKey!) : renderDisplayCell()
      default:
        return renderInputCell()
    }
  } else {
    switch (column.dataKey) {
      case 'sn':
      case 'psu.sn':
      case 'cb.sn':
      case 'hashboards.0.sn':
      case 'hashboards.1.sn':
      case 'hashboards.2.sn':
      case 'hashboards.3.sn':
        return renderSnDisplayCell(column.dataKey == 'sn' ? 'miner' : 'part')
      default:
        return renderDisplayCell()
    }
  }
}

/**
 * Gets display value for a cell using lodash's `get`.
 *
 * @param param0 table column, rowData.
 */
const getDisplayValue = ({ column, rowData }: CellRendererProps) => {
  const dataKey = column.dataKey as keyof ManagedRow<IMinerDetails>

  if (dataKey.endsWith('.model')) {
    const model = _.get(rowData, dataKey) as IModel
    if (!model || !model.name) return 'N/A'
    return generateModelDisplayName(model.name, model.version)
  } else if (dataKey.startsWith('hashboards.')) {
    if (!rowData.hashboards[column.hbNumber]) {
      return 'N/A'
    }
  }
  return _.get(rowData, column.dataKey!) ?? 'N/A'
}

const getIsRecurrent = ({ column, rowData }: { column: Column; rowData: ManagedRow<IMinersTableRowData> }) => {
  const dataKey = column.dataKey as string
  // Build the getter key from the path received up to but not inculing the last
  // bit ('sn'), and replace it with 'has_been_in_rc_before' instead
  return get(rowData, [...initial(dataKey.split('.')), 'has_been_in_rc_before'])
}

/**
 * Renders a miners table row actions cell.
 *
 * @param param0 ElTableV2 rowData.
 */
const minersTableActionsCellRenderer = ({ rowData, rowIndex }: CellRendererProps) => {
  /**
   * Enter cell edit mode.
   */
  const onEnterEditMode = () => {
    editingRowIndex.value = rowIndex
    rowData.updated = null
    rowData.initialValue = _.cloneDeep(rowData)
  }

  /**
   * Toggle edit mode for a row.
   */
  const toggleEditMode = () => {
    if (editingRowIndex.value === rowIndex) {
      onExitEditMode({ rowData })
    } else {
      onEnterEditMode()
    }
  }

  const handleDelete = () => {
    DeleteConfirmationModal(
      'Delete Miner',
      'Are you sure you want to delete this miner? This action cannot be undone',
      rowData.id,
      deleteMinerAsync
    )
  }

  const actionButtons = [
    h(
      ElButton,
      {
        onClick: toggleEditMode,
        link: true,
        disabled: tableIsEditMode.value && editingRowIndex.value !== rowIndex,
      },
      () => [
        h(Icon, {
          icon: editingRowIndex.value === rowIndex ? 'material-symbols:save-outline' : 'material-symbols:edit-rounded',
          height: '16',
        }),
      ]
    ),
  ]

  if (isRcAppManagerUser.value) {
    actionButtons.push(
      h(
        ElButton,
        {
          onClick: handleDelete,
          link: true,
          visible: false,
        },
        () => [
          h(Icon, {
            icon: 'mdi:trash-can-outline',
            height: '16',
          }),
        ]
      )
    )
  }

  return h(
    'div',
    {
      style: {
        display: 'flex',
      },
    },
    actionButtons
  )
}

/**
 * Resets an altered table row's value to initial.
 */
const resetTableRow = ({ rowData }: Pick<CellRendererProps, 'rowData'>) => {
  Object.assign(rowData, rowData.initialValue)
  rowData.initialValue = undefined
}

/**
 * Exit row edit mode, update miner value.
 */
const onExitEditMode = async ({ rowData }: Pick<CellRendererProps, 'rowData'>) => {
  editingRowIndex.value = null
  const updatePayload = {} as Partial<IMinerPartUpdateForm>

  // Handle required miner SN field
  if (!rowData['sn']) {
    rowData['sn'] = rowData.initialValue?.['sn'] ?? ''
    ElMessage.warning('Change to miner SN omitted, miner SN must have a value.')
  }

  // Diff entire row
  const minerDiff = diff(rowData.initialValue!, rowData)

  // Build update payload
  let hashboardModelChanged = false
  for (const columnDiff of minerDiff) {
    const columnDiffRootKey = columnDiff.path[0]

    // Skip "updated" and "initialValue"
    if (columnDiffRootKey === 'updated' || columnDiffRootKey === 'initialValue') {
      continue
    }

    if (columnDiff.type === 'CHANGE' || columnDiff.type === 'CREATE') {
      if (columnDiffRootKey === 'hb') {
        hashboardModelChanged = true
        continue // We handle this afterwards
      } else if (columnDiffRootKey === 'hashboards') {
        if (!updatePayload.hashboards) {
          updatePayload.hashboards = []
        }
        const hashboardNumber = columnDiff.path[1]
        updatePayload.hashboards.push({
          number: +hashboardNumber,
          sn: columnDiff.value || null, // only thing that can change here is the SN
        })
      } else {
        _.set(updatePayload, columnDiff.path, columnDiff.value || null)
      }
    }
  }

  if (hashboardModelChanged) {
    rowData.hashboards.forEach((rowHashboard) => {
      if (!rowHashboard) return
      // Unfortunately, this is necessary as the hashboards aren't guaranteed to be sequential
      // ...Can actually do it in-place above but I'm running out of time and sanity
      const { name, version } = rowData.hb?.model ?? { name: null, version: null }
      const updatedHashboardIndex = updatePayload.hashboards?.findIndex(
        (updateHashboard) => updateHashboard.number === rowHashboard.number
      )
      if (updatedHashboardIndex && updatedHashboardIndex > -1) {
        if (!updatePayload.hashboards) updatePayload.hashboards = []
        _.set(updatePayload.hashboards[updatedHashboardIndex], 'model', { name, version })
      } else {
        if (!updatePayload.hashboards) {
          updatePayload.hashboards = []
        }
        updatePayload.hashboards.push({
          number: rowHashboard.number,
          model: { name, version } as any,
        })
      }
    })
  }

  if (!Object.keys(updatePayload).length) {
    resetTableRow({ rowData })
    return
  }

  try {
    const details = await updateMinerAsync({ id: rowData.id!.toString(), ...updatePayload })
    rowData.updated = 'updated'
    NotifySuccess(`Successfully updated miner "${details.sn}."`, {
      title: 'Updated Miner',
      position: 'bottom-right',
      duration: 3000,
    })
  } catch {
    resetTableRow({ rowData })
    rowData.updated = 'errored'
    NotifyFail(`Failed to update miner "${rowData.sn}."`, {
      title: 'Miner Update Failed',
      position: 'bottom-right',
      duration: 3000,
    })
  } finally {
    rowData.initialValue = undefined
  }
}

/**
 * Handles row (cell) click, navigates to miner repair view in a new tab.
 */
const handleRowClick = ({ rowData }: CellRendererProps) => {
  if (editingRowIndex.value !== null || !editable.value) return
  const route = router.resolve({ name: 'rc-miner-repairs', params: { minerId: rowData.id } })
  window.open(route.href, '_blank', 'noopener,noreferrer')
}

// Todo: possibly add the base update data in here to simplify onExitEditMode
const columns: Column<IMinersTableRowData>[] = [
  {
    title: 'Miner Model',
    key: 'model',
    width: 192,
    fixed: TableV2FixedDir.LEFT,
    cellRenderer: minersTableCellRenderer,
    editable: isRcAppManagerUser.value,
  },
  {
    title: 'Miner SN',
    key: 'sn',
    width: 192,
    fixed: TableV2FixedDir.LEFT,
    cellRenderer: minersTableCellRenderer,
    editable: isRcAppManagerUser.value,
  },
  {
    title: 'Work Order #',
    key: 'work_order_number',
    width: 320,
    hidden: !!props.workOrderNumber,
  },
  { title: 'Customer', key: 'customer', width: 192, hidden: !!props.workOrderNumber },
  {
    title: 'MAC Address',
    key: 'mac_address',
    width: 192,
    fixed: TableV2FixedDir.LEFT,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'PSU Model',
    key: 'psu',
    dataKey: 'psu.model',
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'PSU SN',
    key: 'psu',
    dataKey: 'psu.sn',
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'CB Model',
    key: 'cb',
    dataKey: 'cb.model',
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'CB SN',
    key: 'cb',
    dataKey: 'cb.sn',
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'HB Model',
    key: 'hb',
    dataKey: 'hb.model',
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'HB0 SN',
    key: 'hb',
    dataKey: 'hashboards.0.sn',
    hbNumber: 0,
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'HB1 SN',
    key: 'hb',
    dataKey: 'hashboards.1.sn',
    hbNumber: 1,
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'HB2 SN',
    key: 'hb',
    dataKey: 'hashboards.2.sn',
    hbNumber: 2,
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    title: 'HB3 SN',
    key: 'hb',
    dataKey: 'hashboards.3.sn',
    hbNumber: 3,
    width: 192,
    cellRenderer: minersTableCellRenderer,
    editable: true,
  },
  {
    key: 'id',
    width: 64,
    fixed: TableV2FixedDir.RIGHT,
    cellRenderer: minersTableActionsCellRenderer,
    hidden: !editable.value,
  },
]
  .filter((column) => !column.hidden)
  .map((column) => ({
    ...column,
    dataKey: column.dataKey ?? column.key,
  }))

/**
 * Checks if something is being searched
 */
const isSearch = computed(() => {
  return filters.customer_id || filters.fragment || filters.models || filters.work_order_number
})

const handleCustomerSelectChange = (val: ICustomer | undefined) => {
  filters.customer_id = val?.id
}

const handleWorkOrderSelectChange = (val?: { number?: string }) => {
  filters.work_order_number = val?.number
}

const handleMinerModelSelectChange = (val?: IMinerModel[]) => {
  if (!val || val.length === 0) {
    filters.models = undefined
    return
  }

  filters.models = val?.map((res: IMinerModel) => res.name).join(',')
}

const debouncedPartSnSearch = useDebounceFn((query: string) => {
  filters.part_sn = query.trim() || undefined
}, 500)
</script>

<template>
  <div class="miners-table">
    <div class="miners-table__filters">
      <TableSearchFilter :search-settings="searchSettings" :disabled="tableIsEditMode" />
      <el-input
        v-model="partSnRef"
        placeholder="Part SN"
        clearable
        :prefix-icon="Search"
        style="width: 248px"
        :disabled="tableIsEditMode"
        @input="debouncedPartSnSearch"
      />
      <RlMinerModelSelect :disabled="tableIsEditMode" :max-collapse-tags="0" @change="handleMinerModelSelectChange" />
      <RcCustomerSelect
        v-if="!workOrderNumber"
        style="width: 248px"
        :disabled="tableIsEditMode"
        @change="handleCustomerSelectChange"
      />
      <RcWorkOrderSelect
        v-if="!workOrderNumber"
        style="width: 248px"
        :disabled="tableIsEditMode"
        @change="handleWorkOrderSelectChange"
      />
      <div class="miners-table__controls">
        <slot name="controls"></slot>
      </div>
    </div>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <rl-empty
      v-else-if="tableData.length == 0 && !isFetching && !isSearch"
      title="You have no miners yet"
      subtitle="Miners scanned into work orders will show up here."
    >
    </rl-empty>
    <template v-else>
      <el-auto-resizer :style="{ height: `${tableHeight}px` }">
        <template #default="{ height, width }">
          <el-table-v2
            ref="tableRef"
            v-loading="isFetching"
            :columns="columns"
            :data="tableData"
            :width="width"
            :height="height"
            :cell-props="
              (params: any) => ({
                onClick: params.column.dataKey !== 'id' && (() => handleRowClick(params)),
                style: params.column.dataKey !== 'id' && 'cursor: pointer',
              })
            "
            fixed
          />
        </template>
      </el-auto-resizer>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="miners-table__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching || tableIsEditMode"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.miners-table {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-lg);

  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }

  &__controls {
    flex: 1 1 0%;
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-spacing-xs);
  }

  &__pagination {
    align-self: flex-end;
  }
}

:deep() {
  .updated {
    animation: updated 3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .errored {
    animation: error 3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .nonpresent-part {
    color: var(--text-tertiary);
  }
}

@keyframes updated {
  0% {
    text-shadow: 0 0 14px rgb(11 205 146);
    color: rgb(115 248 207);
  }

  100% {
    text-shadow: 0 0 14px rgb(15 15 15 / 0%);
    color: var(--text-primary);
  }
}

@keyframes error {
  0% {
    text-shadow: 0 0 20px rgb(249 91 0);
    color: rgb(255 172 124);
  }

  100% {
    text-shadow: 0 0 14px rgb(15 15 15 / 0%);
    color: var(--text-primary);
  }
}
</style>
